import { useState } from "react";
import { accountService } from "@miplatform/api";
import { HTTP_STATUSES, MAX_PAGE_SIZE } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IAccountDetailsResponse,
  IApiRequest,
  ISeriesGroupResponse,
  ISeriesResponse,
} from "@miplatform/types";
import { useSeries, useSeriesGroup } from "@mibuildweb/hooks";

export const UNASSIGNED_GROUP_NAME = "Unassigned";

export const useSeriesDetailViewModel = ({
  accountId,
}: {
  accountId: string;
}) => {
  const [accountDetails, setAccountDetails] =
    useState<IAccountDetailsResponse | null>(null);
  const [refresh, setRefresh] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<ISeriesResponse | null>(
    null
  );
  const [selectedSeriesGroup, setSelectedSeriesGroup] =
    useState<ISeriesGroupResponse | null>(null);

  const stateManager = useComponentState();
  const {
    isLoading: isSeriesLoading,
    series,
    getSeries,
    deleteSeries,
  } = useSeries();
  const {
    isLoading: isSeriesGroupLoading,
    seriesGroups,
    getSeriesGroups,
    deleteSeriesGroup,
  } = useSeriesGroup();

  const getAccountDetails = () => {
    return stateManager.run(async () => {
      const response = await accountService.accounts.getAccountById({
        id: accountId,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setAccountDetails(response.data);
      }
      return response;
    });
  };

  const getAccountSeriesGroups = () => {
    return getSeriesGroups({
      sort: "name",
      top: MAX_PAGE_SIZE,
      filters: { accountId: { type: "guid", value: accountId } },
    });
  };

  const getAccountSeries = ({ filters, ...rest }: IApiRequest = {}) => {
    return getSeries({
      ...rest,
      top: MAX_PAGE_SIZE,
      filters: {
        accountId: { type: "guid", value: accountId },
        ...filters,
      },
    });
  };

  const handleDeleteSeries = async (seriesToDelete: ISeriesResponse) => {
    const response = await deleteSeries(seriesToDelete);
    if (response.status === HTTP_STATUSES.OK) {
      setRefresh((prev) => !prev);
    }
  };

  const handleDeleteSeriesGroup = async (seriesGroup: ISeriesGroupResponse) => {
    const response = await deleteSeriesGroup(seriesGroup);
    if (response.status === HTTP_STATUSES.OK) {
      setRefresh((prev) => !prev);
    }
  };

  return {
    isLoading: stateManager.isBusy() || isSeriesLoading || isSeriesGroupLoading,
    accountDetails,
    seriesGroups,
    series,
    refresh,
    setRefresh,
    getAccountDetails,
    getAccountSeriesGroups,
    getAccountSeries,
    handleDeleteSeries,
    handleDeleteSeriesGroup,
    selectedSeries,
    setSelectedSeries,
    selectedSeriesGroup,
    setSelectedSeriesGroup,
  };
};
